import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getAiStatus,
  getAiConversations,
  getAiConversationMessages,
  deleteAiConversation,
} from '../services/api';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { streamAiChat } from '../utils/aiChatStream';
import { renderMarkdown } from '../utils/miniMarkdown';
import { formatDateRelative } from '../utils/helpers';

// The CRM Assistant: a round button bottom-right that opens a chat panel
// (docs/ai-module-design-2026-10-30.md §8.2). Read-only helper — it never
// changes data. Mounted once in App.js (inside <Router>, after every
// compulsory-setup gate). Styles: styles/aiChat.css (global, see pageStyles.js).

const STATUS_POLL_MS = 5 * 60 * 1000;
const LS_OPEN = 'aiChat.open';
const LS_CONV = 'aiChat.conversationId';
const MOBILE_QUERY = '(max-width: 768px)';

// localStorage can throw (private window, blocked site data): never let it break the chat.
const lsGet = (k) => { try { return window.localStorage.getItem(k); } catch (e) { return null; } };
const lsSet = (k, v) => {
  try {
    if (v === null || v === undefined) window.localStorage.removeItem(k);
    else window.localStorage.setItem(k, String(v));
  } catch (e) { /* ignore */ }
};

const readMobile = () => {
  try { return window.matchMedia(MOBILE_QUERY).matches; } catch (e) { return false; }
};

// Tool chip wording: the server sends the present-tense label ("Checking the
// order"); a finished chip reads "✓ Checked the order".
const PAST = {
  Checking: 'Checked', Looking: 'Looked', Searching: 'Searched', Listing: 'Listed', Reading: 'Read',
  Running: 'Ran', Finding: 'Found', Auditing: 'Audited', Reconciling: 'Reconciled', Summarising: 'Summarised',
  Summarizing: 'Summarized', Fetching: 'Fetched', Loading: 'Loaded', Comparing: 'Compared',
  Calculating: 'Calculated', Querying: 'Queried', Inspecting: 'Inspected', Reviewing: 'Reviewed',
};
const stripDots = (label) => String(label || 'Working').replace(/[….]+$/, '');
const doneLabel = (label) => stripDots(label).replace(/^\w+/, (w) => PAST[w] || w);

const CAP_MESSAGE = "Today's AI budget for your role has been used up. The assistant is back tomorrow; an administrator can raise the daily limit.";
const FULL_MESSAGE = 'This chat is long — start a new one.';

const errorMessage = (e) => {
  if (e && e.name === 'TypeError') return 'Could not reach the server. Check your connection and try again.';
  return (e && (e.error || e.message)) || 'Something went wrong. Please try again.';
};

let itemSeq = 0;
const nextId = (p) => { itemSeq += 1; return `${p}${itemSeq}`; };

const Markdown = React.memo(function Markdown({ text, onNavigate }) {
  return <div className="aic-md">{renderMarkdown(text, { onNavigate })}</div>;
});

const historyToItems = (rows) => (rows || []).map((r) => ({
  id: `m${r.id}`,
  role: r.role === 'user' ? 'user' : 'assistant',
  text: r.text || '',
  tools: (r.tools || []).map((t, i) => ({ id: `h${r.id}-${i}`, name: t.name, label: t.label, status: t.ok === false ? 'error' : 'done' })),
  notices: r.notice ? [r.notice] : [],
  error: null,
  streaming: false,
}));

function AiChatBubble() {
  const navigate = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState(null);
  const [isMobile, setIsMobile] = useState(readMobile);
  const [open, setOpen] = useState(() => !readMobile() && lsGet(LS_OPEN) === '1');
  const [items, setItems] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [convModel, setConvModel] = useState(null);
  const [historyState, setHistoryState] = useState({ loading: false, error: null, hasMore: false, page: 1 });
  const [drawer, setDrawer] = useState(false);
  const [convs, setConvs] = useState({ rows: [], page: 0, total: 0, loading: false, error: null });

  const convIdRef = useRef(Number(lsGet(LS_CONV)) || null);
  const loadedConvRef = useRef(null); // conversation whose history is on screen
  const streamingRef = useRef(false);
  const runRef = useRef(0);
  const abortRef = useRef(null);
  const pendingRef = useRef({ aid: null, text: '' });
  const rafRef = useRef(0);
  const bodyRef = useRef(null);
  const panelRef = useRef(null);
  const textareaRef = useRef(null);
  const stickRef = useRef(true);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // ---- status ----------------------------------------------------------
  const fetchStatus = useCallback(async () => {
    try {
      const s = await getAiStatus();
      if (s && typeof s === 'object') setStatus(s);
    } catch (e) {
      // offline / mid-deploy: keep whatever we knew, the next tick retries
    }
  }, []);
  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useAutoRefresh(fetchStatus, { intervalMs: STATUS_POLL_MS });

  const capReached = !!status && status.reason === 'cap_reached';
  // Administrators see the bubble even while the assistant is off, with the reason and a link to set it up.
  const setupNeeded = !!status && !status.enabled && !capReached && status.is_admin === true;
  const setupText = !setupNeeded ? '' : (
    status.reason === 'no_key' ? 'No Anthropic API key is saved yet, so the assistant is off for everyone.'
      : status.reason === 'key_error' ? 'The saved API key is failing. Test or replace it on the AI Assistant page.'
        : 'The assistant is switched off for administrators. Enable the Administrator role on the AI Assistant page.'
  );
  const visible = !!status && (status.enabled || capReached || setupNeeded);

  // ---- viewport --------------------------------------------------------
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(MOBILE_QUERY); } catch (e) { return undefined; }
    const on = () => setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);

  // Full-screen panel on phones: the page behind must not scroll.
  useEffect(() => {
    if (!(visible && open && isMobile)) return undefined;
    const html = document.documentElement;
    const prevBody = document.body.style.overflow;
    const prevHtml = html.style.overflow;
    document.body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBody;
      html.style.overflow = prevHtml;
    };
  }, [visible, open, isMobile]);

  // Leaving (logout, gate flips): stop any answer still being written.
  useEffect(() => () => {
    runRef.current += 1;
    if (abortRef.current) abortRef.current.abort();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  // ---- helpers ---------------------------------------------------------
  const setConv = (id) => {
    convIdRef.current = id || null;
    lsSet(LS_CONV, id || null);
  };

  const scrollToBottom = () => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  useEffect(() => {
    if (stickRef.current) scrollToBottom();
  }, [items, historyState.loading, drawer, open]);

  const onBodyScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const openPanel = () => { setOpen(true); lsSet(LS_OPEN, '1'); };
  const closePanel = useCallback(() => { setOpen(false); setDrawer(false); lsSet(LS_OPEN, '0'); }, []);

  useEffect(() => {
    if (!(visible && open)) return;
    const el = isMobile ? panelRef.current : textareaRef.current;
    if (el) el.focus({ preventScroll: true });
  }, [visible, open, isMobile]);

  // Links the bot wrote: navigate in-app; on a phone the panel would cover the page, so collapse it.
  const goTo = useCallback((route) => {
    navigate(route.path, route.state ? { state: route.state } : undefined);
    if (readMobile()) closePanel();
  }, [navigate, closePanel]);

  // ---- history ---------------------------------------------------------
  const loadHistory = useCallback(async (id, page = 1) => {
    setHistoryState((h) => ({ ...h, loading: true, error: null }));
    try {
      const res = await getAiConversationMessages(id, page);
      if (convIdRef.current !== id) return;
      const older = historyToItems(res && res.rows);
      if (page === 1) {
        stickRef.current = true;
        setItems(older);
      } else {
        stickRef.current = false;
        setItems((prev) => [...older, ...prev]);
      }
      loadedConvRef.current = id;
      setHistoryState({ loading: false, error: null, hasMore: !!(res && res.has_more), page });
    } catch (e) {
      if (convIdRef.current !== id) return;
      if (e && e.response && e.response.status === 404) {
        setConv(null);
        loadedConvRef.current = null;
        setItems([]);
        setHistoryState({ loading: false, error: null, hasMore: false, page: 1 });
      } else {
        setHistoryState((h) => ({ ...h, loading: false, error: 'Could not load this chat.' }));
      }
    }
  }, []);

  useEffect(() => {
    if (!(visible && open)) return;
    const id = convIdRef.current;
    if (id && loadedConvRef.current !== id && !streamingRef.current) loadHistory(id, 1);
  }, [visible, open, loadHistory]);

  // ---- conversations drawer -------------------------------------------
  const loadConvs = useCallback(async (page) => {
    setConvs((c) => ({ ...c, loading: true, error: null }));
    try {
      const res = await getAiConversations(page);
      setConvs((c) => ({
        rows: page === 1 ? (res.rows || []) : [...c.rows, ...(res.rows || [])],
        page,
        total: res.total || 0,
        loading: false,
        error: null,
      }));
    } catch (e) {
      setConvs((c) => ({ ...c, loading: false, error: 'Could not load your chats.' }));
    }
  }, []);

  const toggleDrawer = () => {
    if (!drawer) loadConvs(1);
    setDrawer(!drawer);
  };

  // ---- streaming -------------------------------------------------------
  const patchItem = (id, fn) => setItems((prev) => prev.map((it) => (it.id === id ? fn(it) : it)));

  const flushText = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    const { aid, text } = pendingRef.current;
    if (!aid || !text) return;
    pendingRef.current = { aid, text: '' };
    patchItem(aid, (it) => ({ ...it, text: it.text + text }));
  };

  const stopStream = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const resetView = () => {
    // Drop whatever is on screen (and any answer still arriving for it).
    runRef.current += 1;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    pendingRef.current = { aid: null, text: '' };
    streamingRef.current = false;
    setStreaming(false);
    setItems([]);
    setConvModel(null);
    setHistoryState({ loading: false, error: null, hasMore: false, page: 1 });
    loadedConvRef.current = null;
    stickRef.current = true;
  };

  const newChat = () => {
    resetView();
    setConv(null);
    setDrawer(false);
    setInput('');
    if (textareaRef.current && !isMobile) textareaRef.current.focus();
  };

  const openConversation = (id) => {
    resetView();
    setConv(id);
    setDrawer(false);
    loadHistory(id, 1);
  };

  const removeConversation = async (c) => {
    if (!window.confirm(`Delete this chat?\n\n${c.title || 'Untitled chat'}`)) return;
    try {
      await deleteAiConversation(c.id);
      setConvs((cur) => ({ ...cur, rows: cur.rows.filter((r) => r.id !== c.id), total: Math.max(0, cur.total - 1) }));
      if (convIdRef.current === c.id) newChat();
    } catch (e) {
      setConvs((cur) => ({ ...cur, error: 'Could not delete that chat.' }));
    }
  };

  const send = async (rawText) => {
    const text = String(rawText === undefined ? input : rawText).trim();
    if (!text || streamingRef.current || capReached || setupNeeded) return;
    const myRun = runRef.current + 1;
    runRef.current = myRun;
    const userId = nextId('u');
    const aid = nextId('a');
    const controller = new AbortController();
    abortRef.current = controller;
    streamingRef.current = true;
    stickRef.current = true;
    pendingRef.current = { aid, text: '' };
    setInput('');
    setStreaming(true);
    setItems((prev) => [
      ...prev,
      { id: userId, role: 'user', text, tools: [], notices: [], error: null, streaming: false },
      { id: aid, role: 'assistant', text: '', tools: [], notices: [], error: null, streaming: true, userItemId: userId },
    ]);

    const live = () => runRef.current === myRun;
    let finished = false;

    const fail = (code, message, httpStatus) => {
      flushText();
      patchItem(aid, (it) => ({ ...it, streaming: false, error: { code, message, status: httpStatus, retryText: text } }));
    };

    const onEvent = (ev) => {
      if (!live() || !ev) return;
      switch (ev.type) {
        case 'start':
          if (ev.conversation_id) {
            setConv(ev.conversation_id);
            loadedConvRef.current = ev.conversation_id;
          }
          if (ev.model && ev.model.display_name) setConvModel(ev.model.display_name);
          break;
        case 'text':
          if (typeof ev.delta === 'string' && ev.delta) {
            pendingRef.current = { aid, text: pendingRef.current.text + ev.delta };
            if (!rafRef.current) {
              rafRef.current = requestAnimationFrame(() => { rafRef.current = 0; flushText(); });
            }
          }
          break;
        case 'tool':
          flushText();
          patchItem(aid, (it) => {
            const tools = it.tools.slice();
            const at = tools.findIndex((t) => t.id === ev.id);
            const next = { id: ev.id, name: ev.name, label: ev.label || (at >= 0 ? tools[at].label : ''), status: ev.status };
            if (at >= 0) tools[at] = next; else tools.push(next);
            return { ...it, tools };
          });
          break;
        case 'notice':
          flushText();
          if (ev.message) patchItem(aid, (it) => ({ ...it, notices: [...it.notices, String(ev.message)] }));
          break;
        case 'done':
          finished = true;
          flushText();
          patchItem(aid, (it) => ({
            ...it,
            streaming: false,
            tools: it.tools.map((t) => (t.status === 'running' ? { ...t, status: 'done' } : t)),
          }));
          break;
        case 'error':
          finished = true;
          fail(ev.code, ev.message || 'The assistant could not answer.');
          break;
        default:
          break;
      }
    };

    try {
      await streamAiChat({
        conversationId: convIdRef.current,
        message: text,
        pagePath: pathRef.current,
        onEvent,
        signal: controller.signal,
      });
      if (live() && !finished) {
        flushText();
        patchItem(aid, (it) => (it.text
          ? { ...it, streaming: false, notices: [...it.notices, 'The answer was cut off.'] }
          : { ...it, streaming: false, error: { code: 'AI_UPSTREAM', message: 'The connection closed before an answer arrived.', retryText: text } }));
      }
    } catch (e) {
      if (live()) {
        if (e && e.name === 'AbortError') {
          flushText();
          patchItem(aid, (it) => ({
            ...it,
            streaming: false,
            tools: it.tools.map((t) => (t.status === 'running' ? { ...t, status: 'error' } : t)),
            notices: [...it.notices, 'Stopped.'],
          }));
        } else {
          fail(e && e.code, errorMessage(e), e && e.status);
        }
      }
    } finally {
      if (live()) {
        abortRef.current = null;
        streamingRef.current = false;
        setStreaming(false);
        fetchStatus(); // spend / daily cap may have changed
      }
    }
  };

  const retry = (it) => {
    const e = it.error;
    if (!e || streamingRef.current) return;
    const fresh = e.code === 'AI_ROLE_CHANGED' || e.status === 404;
    if (fresh) {
      resetView();
      setConv(null);
    } else {
      setItems((prev) => prev.filter((x) => x.id !== it.id && x.id !== it.userItemId));
    }
    send(e.retryText);
  };

  const newChatWithText = (it) => {
    const text = it.error && it.error.retryText;
    newChat();
    if (text) setInput(text);
  };

  // ---- keyboard --------------------------------------------------------
  const onPanelKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    if (drawer) setDrawer(false); else closePanel();
  };

  const onInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isMobile && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [input, open]);

  if (!visible) return null;

  const modelName = convModel || (status.model && status.model.display_name) || '';
  const empty = items.length === 0 && !historyState.loading && !historyState.error;
  const prompts = Array.isArray(status.quick_prompts) ? status.quick_prompts : [];

  const fab = (
    <button
      type="button"
      className={`aic-fab${capReached ? ' aic-fab--capped' : ''}`}
      aria-label="Open CRM Assistant"
      title={capReached ? CAP_MESSAGE : setupNeeded ? 'CRM Assistant (not set up yet)' : 'CRM Assistant'}
      onClick={openPanel}
    >
      <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M4 3h16a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H9l-5 4v-4H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm3 5v2h10V8H7zm0 4v2h7v-2H7z" />
      </svg>
    </button>
  );

  if (!open) return <div className="aic-root">{fab}</div>;

  return (
    <div className="aic-root">
      <section
        className="aic-panel"
        role="dialog"
        aria-label="CRM Assistant"
        tabIndex={-1}
        ref={panelRef}
        onKeyDown={onPanelKeyDown}
      >
        <header className="aic-head">
          <div className="aic-title">
            <span className="aic-title-text">CRM Assistant</span>
            {modelName && <span className="aic-chip" title="Model answering in this chat">{modelName}</span>}
          </div>
          <div className="aic-head-actions">
            <button type="button" className="aic-iconbtn" aria-label="Past conversations" aria-expanded={drawer} title="Past conversations" onClick={toggleDrawer}>☰</button>
            <button type="button" className="aic-iconbtn" aria-label="New chat" title="New chat" onClick={newChat}>＋</button>
            <button type="button" className="aic-iconbtn" aria-label="Close assistant" title="Close" onClick={closePanel}>×</button>
          </div>
        </header>

        {drawer && (
          <div className="aic-drawer" aria-label="Past conversations">
            <div className="aic-drawer-head">Your chats</div>
            {convs.rows.length === 0 && !convs.loading && !convs.error && <p className="aic-muted">No chats yet.</p>}
            <ul className="aic-conv-list">
              {convs.rows.map((c) => (
                <li key={c.id} className={`aic-conv${c.id === convIdRef.current ? ' aic-conv--current' : ''}`}>
                  <button type="button" className="aic-conv-open" onClick={() => openConversation(c.id)}>
                    <span className="aic-conv-title">{c.title || 'Untitled chat'}</span>
                    <span className="aic-conv-date">{formatDateRelative(c.last_message_at || c.created_at)}</span>
                  </button>
                  <button type="button" className="aic-iconbtn aic-conv-del" aria-label={`Delete chat ${c.title || ''}`} title="Delete" onClick={() => removeConversation(c)}>🗑</button>
                </li>
              ))}
            </ul>
            {convs.error && <p className="aic-error-text">{convs.error}</p>}
            {convs.loading && <p className="aic-muted">Loading…</p>}
            {!convs.loading && convs.rows.length < convs.total && (
              <button type="button" className="aic-link-btn" onClick={() => loadConvs(convs.page + 1)}>Load more</button>
            )}
          </div>
        )}

        <div className="aic-body" ref={bodyRef} onScroll={onBodyScroll} role="log" aria-live="polite" aria-label="Conversation">
          {historyState.hasMore && !historyState.loading && (
            <button type="button" className="aic-link-btn aic-earlier" onClick={() => loadHistory(convIdRef.current, historyState.page + 1)}>Load earlier messages</button>
          )}
          {historyState.loading && <p className="aic-muted aic-center">Loading chat…</p>}
          {historyState.error && (
            <p className="aic-error-text aic-center">
              {historyState.error}{' '}
              <button type="button" className="aic-link-btn" onClick={() => loadHistory(convIdRef.current, 1)}>Retry</button>
            </p>
          )}

          {empty && setupNeeded && (
            <div className="aic-empty">
              <p className="aic-empty-hello">{setupText}</p>
              <div className="aic-prompts">
                <button type="button" className="aic-prompt" onClick={() => { navigate('/ai-assistant'); if (isMobile) setOpen(false); }}>Open AI Assistant settings</button>
              </div>
            </div>
          )}

          {empty && !setupNeeded && (
            <div className="aic-empty">
              <p className="aic-empty-hello">Ask me how something works in the CRM, or to check a record.</p>
              {prompts.length > 0 && (
                <div className="aic-prompts">
                  {prompts.map((p) => (
                    <button key={p} type="button" className="aic-prompt" disabled={capReached || streaming} onClick={() => send(p)}>{p}</button>
                  ))}
                </div>
              )}
              <p className="aic-privacy">
                I read the CRM but cannot change anything. Chats are kept for {status.retention_days || 30} days and visible to administrators.
              </p>
            </div>
          )}

          {items.map((it) => (it.role === 'user' ? (
            <div key={it.id} className="aic-msg aic-msg--user">
              <div className="aic-bubble aic-bubble--user">{it.text}</div>
            </div>
          ) : (
            <div key={it.id} className="aic-msg aic-msg--assistant">
              {(it.text || (it.streaming && it.tools.length === 0)) && (
                <div className="aic-bubble aic-bubble--assistant">
                  {it.text
                    ? <Markdown text={it.text} onNavigate={goTo} />
                    : <span className="aic-typing" role="status" aria-label="Thinking"><i /><i /><i /></span>}
                </div>
              )}
              {it.tools.length > 0 && (
                <div className="aic-tools">
                  {it.tools.map((t) => (
                    <span key={t.id} className={`aic-tool aic-tool--${t.status}`}>
                      {t.status === 'running' && <span className="aic-spin" aria-hidden="true" />}
                      {t.status === 'running' ? `${stripDots(t.label)}…` : `${t.status === 'error' ? '✕' : '✓'} ${t.status === 'error' ? stripDots(t.label) : doneLabel(t.label)}`}
                    </span>
                  ))}
                </div>
              )}
              {it.notices.map((n, i) => <div key={i} className="aic-notice">{n}</div>)}
              {it.error && (
                <div className="aic-error" role="alert">
                  <span>{it.error.code === 'AI_CONVERSATION_FULL' ? FULL_MESSAGE : it.error.message}</span>
                  {it.error.code === 'AI_CONVERSATION_FULL'
                    ? <button type="button" className="aic-btn" onClick={() => newChatWithText(it)}>Start a new chat</button>
                    : <button type="button" className="aic-btn" onClick={() => retry(it)} disabled={streaming}>Retry</button>}
                </div>
              )}
            </div>
          )))}
        </div>

        <footer className="aic-foot">
          {capReached ? (
            <p className="aic-capped" role="status">{CAP_MESSAGE}</p>
          ) : setupNeeded ? (
            <p className="aic-capped" role="status">Not set up yet — administrators only see this bubble.</p>
          ) : (
            <>
              <textarea
                ref={textareaRef}
                className="aic-input"
                rows={1}
                maxLength={8000}
                value={input}
                placeholder="Ask about the CRM…"
                aria-label="Message to the assistant"
                enterKeyHint={isMobile ? 'enter' : 'send'}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onInputKeyDown}
              />
              {streaming ? (
                <button type="button" className="aic-send aic-send--stop" aria-label="Stop answering" onClick={stopStream}>Stop</button>
              ) : (
                <button type="button" className="aic-send" aria-label="Send message" disabled={!input.trim()} onClick={() => send()}>Send</button>
              )}
            </>
          )}
        </footer>
      </section>
    </div>
  );
}

export default AiChatBubble;
